import React, { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useParams } from "react-router";
import GoogleAnalyticsReporter from "../utils/GoogleAnalyticsReporter.js";
import "../utils/popUp.css";
import { useBranding } from "../context/BrandingContext.jsx";
import { useSelector } from "react-redux";
import { Slot, ThemeLoader, useTheme } from "../themes/ThemeContext.jsx";
import CertificateView from "../pages/CertificateView.jsx";

import PayPal from "../pages/PayPal.jsx";

// Lazy-loaded components
const LessonDisplay = lazy(() => import("../components/LessonDisplay.jsx"));
const ExamDisplay = lazy(() => import("../components/ExamDisplay.jsx"));
const Login = lazy(() => import("../pages/Login.jsx"));
const OrgLogin = lazy(() => import("../pages/OrgLogin.jsx"));
const Register = lazy(() => import("../pages/Register.jsx"));
const PrivateRoute = lazy(() => import("../components/PrivateRoute"));
const AdminRoutes = lazy(() => import("../components/AdminRoutes.jsx"));
const Profile = lazy(() => import("../pages/Profile.jsx"));
const PublicRoute = lazy(() => import("../components/PublicRoute.jsx"));
const ForgotPassword = lazy(() => import("../pages/ForgotPassword.jsx"));
const ResetPassword = lazy(() => import("../pages/ResetPassword.jsx"));
const VerifyEmail = lazy(() => import("../pages/VerifyEmail.jsx"));
const Worlds = lazy(() => import("../pages/Worlds.jsx"));
const AppShell = lazy(() => import("../components/AppShell.jsx"));
const AdminDashboard = lazy(() => import("../pages/AdminDashboard.jsx"));
const CourseContentManager = lazy(() =>
  import("../pages/CourseContentManager.jsx")
);
const WhatsappCourses = lazy(() => import("../pages/WhatsappCourses.jsx"));
const GuideEditor = lazy(() => import("../pages/GuideEditor.jsx"));

const trackingId = "G-FVDDHYS4T0";

const LegacyCourseRedirect = () => {
  const { id, challengeId } = useParams();
  const courseId = id || challengeId;

  return (
    <Navigate
      to={courseId ? `/dashboard/challenge/${courseId}/content` : "/dashboard"}
      replace
    />
  );
};

// Cuando el dominio pertenece a una organización (ej. aclara.ewaffle.cl), "/" y
// "/login" muestran directamente el login de esa org; el genérico de Ewaffle
// queda para app.ewaffle.cl y para el fallback por slug (/o/:slug/login).
const BrandLoader = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="w-5 h-5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const HostAwareLogin = () => {
  const { hostOrg, hostResolved } = useBranding();
  if (!hostResolved) return <BrandLoader />;
  if (hostOrg?.slug) return <OrgLogin slug={hostOrg.slug} hostMode />;
  return <Login />;
};

const HostAwareRegister = () => {
  const { hostOrg, hostResolved } = useBranding();
  if (!hostResolved) return <BrandLoader />;
  if (hostOrg?.slug) return <Navigate to={`/o/${hostOrg.slug}/register`} replace />;
  return <Register />;
};

// Rutas extra que declara el tema de la org (ej. /noticias, /equipo).
const themeRoutes = (list) =>
  (list || []).map((route) => <Route key={route.path} path={route.path} element={route.element} />);

// Comodín dentro del shell: un enlace directo o un refresh en una ruta del tema
// llega antes de que la org de la sesión (y con ella el tema) esté cargada.
// Mientras tanto se muestra el loader; cuando el tema entra, sus rutas se
// montan y la URL se resuelve sola. Sin tema, se cae al home clásico.
const ThemeRouteFallback = () => {
  const { loading } = useTheme();
  const company = useSelector((state) => state.company);
  if (loading || !company?._id) return <ThemeLoader />;
  return <Worlds />;
};

function App() {
  const { theme, sessionPending } = useTheme();
  // Con sesión y sin saber aún la org (y su tema), las rutas del tema no
  // existen y el comodín pintaría la UI por defecto para luego saltar: mejor
  // un loader. Solo en ese caso: el login público resuelve su org por slug y
  // desmontarlo aquí la perdería.
  if (sessionPending) return <ThemeLoader />;
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="flex items-center gap-3 text-white">
            <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            Cargando...
          </div>
        </div>
      }
    >
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicRoute />}>
          <Route path="/" element={<HostAwareLogin />} />
          <Route path="/login" element={<HostAwareLogin />} />
          <Route path="/o/:orgSlug/login" element={<OrgLogin />} />
          <Route path="/register" element={<HostAwareRegister />} />
          <Route path="/o/:orgSlug/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/verify/:code" element={<CertificateView publicMode />} />
          {themeRoutes(theme?.publicRoutes)}
        </Route>

        {/* Private Routes wrapped in AppShell */}
        <Route element={<PrivateRoute />}>
          <Route element={<AppShell />}>
            <Route path="/home" element={<Slot name="Home" fallback={<Worlds />} />} />
            <Route path="/profile" element={<Slot name="Profile" fallback={<Profile />} />} />
            <Route path="/certificate/:code" element={<CertificateView />} />
            <Route path="/dashboard" element={<Slot name="Dashboard" fallback={<AdminDashboard />} />} />
            <Route
              path="challenge/:id/:stageId/:contentId"
              element={<Slot name="Lesson" fallback={<LessonDisplay />} />}
            />
            <Route
              path="challenge/:challengeId/:stageId/exam/:examId"
              element={<Slot name="Exam" fallback={<ExamDisplay />} />}
            />
            {themeRoutes(theme?.routes)}
            <Route path="*" element={<ThemeRouteFallback />} />

            {/* Admin-only routes */}
            <Route element={<AdminRoutes />}>
              <Route
                path="/whatsapp-courses"
                element={<WhatsappCourses />}
              />
              <Route path="/challenge/:id" element={<LegacyCourseRedirect />} />
              <Route
                path="/dashboard/challenge/:id/content"
                element={<CourseContentManager />}
              />
              <Route
                path="/dashboard/challenge/:id/guide/:guideId"
                element={<GuideEditor />}
              />
              <Route
                path="/challenge/:id/details"
                element={<LegacyCourseRedirect />}
              />
              <Route
                path="challenge/:id/:stageId/add-content"
                element={<LegacyCourseRedirect />}
              />
              <Route
                path="challenge/:challengeId/:stageId/add-quiz"
                element={<LegacyCourseRedirect />}
              />
              <Route
                path="challenge/:challengeId/:stageId/:contentId/edit-content"
                element={<LegacyCourseRedirect />}
              />
              <Route
                path="challenge/:challengeId/:stageId/:contentId/edit-exam"
                element={<LegacyCourseRedirect />}
              />
            </Route>
          </Route>
        </Route>

        {/* Other Routes */}
        <Route path="/adminIn" element={<Navigate to="/dashboard" replace />} />
        <Route path="/paypal" element={<PayPal />} />
        <Route path="*" element={<Worlds />} />
      </Routes>
      <GoogleAnalyticsReporter trackingId={trackingId} />
    </Suspense>
  );
}

export default App;
